// ==============================
// File: src/pages/Forum.jsx
// Description: Lightweight forum with threads & replies
// Persistence:
//  - If StorageContext is available → saves to "forum/threads.json"
//  - Otherwise falls back to localStorage ("mudoForumThreads")
// Features:
//  - Text threads with replies
//  - File and image attachments
//  - Image preview in threads
// ==============================
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Star, StarOff } from "lucide-react";
import { useNotifications } from "../contexts/NotificationsContext";
import { useForumSubscriptions } from "../contexts/ForumSubscriptionsContext";
import { ROUTES } from "../lib/nav";
import ReportPostModal from "../components/Forum/ReportPostModal";
import { useAuth } from "../contexts/AuthContext";
import { supabase, hasSupabase } from "../supabaseClient";

// Optional: use StorageContext if app provides it
let useStorage;
try {
  // eslint-disable-next-line import/no-unresolved
  ({ useStorage } = require("@/contexts/StorageContext"));
} catch {
  useStorage = null;
}

const shell = { padding: 16, color: "#fff" };
const header = {
  display: "flex",
  flexWrap: "wrap",
  gap: 12,
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 12,
};
const h1 = { margin: 0, fontSize: 22, fontWeight: 800 };
const btn = {
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.08)",
  color: "#fff",
  fontSize: 13,
  cursor: "pointer",
};
const input = {
  padding: "8px 10px",
  minWidth: 200,
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.06)",
  color: "#fff",
  outline: "none",
  fontSize: 13,
};
const area = { ...input, minWidth: 280, minHeight: 80, resize: "vertical" };

const card = {
  padding: 12,
  borderRadius: 14,
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
};
const row = { ...card, marginBottom: 10 };
const title = { margin: 0, fontWeight: 700, fontSize: 16 };
const meta = { margin: 0, opacity: 0.75, fontSize: 12 };
const reply = {
  padding: 10,
  borderRadius: 10,
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.1)",
};

function nowIso() { return new Date().toISOString(); }
function fromIso(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }
function nanoid() { return Math.random().toString(36).slice(2) + Date.now().toString(36); }

const LS_KEY = "mudoForumThreads";
const FILE_PATH = "forum/threads.json";

export default function Forum() {
  const storage = useStorage ? useStorage() : null;
  const { user: currentUser } = useAuth() || {};
  const { addNotification } = useNotifications() || {};
  const { isFollowing, toggleFollow } = useForumSubscriptions();
  const location = useLocation();

  const [threads, setThreads] = useState([]);
  const [query, setQuery] = useState("");
  const [expandId, setExpandId] = useState(null);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportingPost, setReportingPost] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Forum categories with unique colors
  const CATEGORIES = [
    { id: "all", label: "All Discussions", color: "#94a3b8" },
    { id: "techniques", label: "Techniques & Tips", color: "#a78bfa" },
    { id: "materials", label: "Materials & Tools", color: "#60a5fa" },
    { id: "kiln", label: "Kiln & Firing", color: "#f87171" },
    { id: "business", label: "Business & Cost", color: "#34d399" },
    { id: "planner", label: "Planner & Features", color: "#fbbf24" },
    { id: "general", label: "General Chat", color: "#fb923c" },
  ];

  // create thread form
  const [tTitle, setTTitle] = useState("");
  const [tBody, setTBody] = useState("");
  const [tCategory, setTCategory] = useState("general");
  const [tAttachments, setTAttachments] = useState([]); // { name, data (base64), type, size }
  const threadFileInputRef = useRef(null);

  // reply form (per thread id)
  const [replyText, setReplyText] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const replyFileInputRef = useRef(null);

  // Load on mount
  useEffect(() => {
    let isMounted = true;
    (async () => {
      setIsLoading(true);
      try {
        // Prefer Supabase shared forum if available
        if (hasSupabase() && supabase) {
          const { data, error } = await supabase
            .from("forum_threads")
            .select(
              "id,title,body,category,attachments,author_user_id,author_email,author_display_name,created_at, forum_replies(id,body,attachments,author_user_id,author_email,author_display_name,created_at)"
            )
            .order("created_at", { ascending: false });
          if (error) throw error;

          const mapped = (data || []).map((t) => ({
            id: t.id,
            title: t.title,
            body: t.body,
            category: t.category || "general",
            attachments: Array.isArray(t.attachments) ? t.attachments : [],
            author_user_id: t.author_user_id,
            author_email: t.author_email,
            author_display_name: t.author_display_name,
            createdAt: t.created_at,
            replies: (t.forum_replies || []).map((r) => ({
              id: r.id,
              body: r.body,
              attachments: Array.isArray(r.attachments) ? r.attachments : [],
              author_user_id: r.author_user_id,
              author_email: r.author_email,
              author_display_name: r.author_display_name,
              createdAt: r.created_at,
            })),
          }));
          if (isMounted) setThreads(mapped);
        } else if (storage?.loadJSON) {
          const data = await storage.loadJSON(FILE_PATH, []);
          if (isMounted) setThreads(Array.isArray(data) ? data : []);
        } else {
          const saved = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
          if (isMounted) setThreads(saved);
        }
      } catch (err) {
        console.error("[Forum] loadThreads error", err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist whenever threads change
  useEffect(() => {
    (async () => {
      // Only persist locally when Supabase is not available
      if (!hasSupabase() || !supabase) {
        if (storage?.saveJSON) {
          try { await storage.saveJSON(FILE_PATH, threads); } catch (err) { console.error("[Forum] persist error", err); }
        }
        try { localStorage.setItem(LS_KEY, JSON.stringify(threads)); } catch {}
      }
    })();
  }, [threads, storage]);

  // Expand a thread when deep-linked via ?thread=ID (e.g., from inbox notification)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      const target = params.get("thread") || params.get("threadId");
      if (target) setExpandId(target);
    } catch {}
  }, [location.search]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let result = threads;
    
    // Filter by category
    if (selectedCategory !== "all") {
      result = result.filter((t) => (t.category || "general") === selectedCategory);
    }
    
    // Filter by search query
    if (q) {
      result = result.filter(
        (t) =>
          (t.title || "").toLowerCase().includes(q) ||
          (t.body || "").toLowerCase().includes(q) ||
          (t.replies || []).some((r) => (r.body || "").toLowerCase().includes(q))
      );
    }
    
    return result;
  }, [threads, query, selectedCategory]);

  const createThread = async () => {
    const title = tTitle.trim();
    const body = tBody.trim();
    if (!title && !body) return;

    // Prefer Supabase shared insert
    if (hasSupabase() && supabase) {
      try {
        const { data, error } = await supabase
          .from("forum_threads")
          .insert({
            title,
            body,
            category: tCategory,
            attachments: tAttachments,
            author_user_id: currentUser?.id || null,
            author_email: currentUser?.email || null,
            author_display_name: currentUser?.user_metadata?.full_name || currentUser?.email || null,
          })
          .select()
          .single();
        if (error) throw error;

        const mapped = {
          id: data.id,
          title: data.title,
          body: data.body,
          category: data.category || "general",
          attachments: Array.isArray(data.attachments) ? data.attachments : [],
          author_user_id: data.author_user_id,
          author_email: data.author_email,
          author_display_name: data.author_display_name,
          createdAt: data.created_at,
          replies: [],
        };
        setThreads((prev) => [mapped, ...prev]);
        setExpandId(mapped.id);
      } catch (err) {
        console.warn("[Forum] Supabase createThread failed, falling back to local", err);
        const th = {
          id: (crypto?.randomUUID?.() || nanoid()),
          title,
          body,
          category: tCategory,
          createdAt: nowIso(),
          created_at: nowIso(),
          author_user_id: currentUser?.id || null,
          author_email: currentUser?.email || null,
          replies: [],
          attachments: tAttachments,
        };
        const next = [th, ...threads];
        setThreads(next);
        setExpandId(th.id);
        (async () => {
          try {
            if (storage?.saveJSON) {
              await storage.saveJSON(FILE_PATH, next);
            } else {
              localStorage.setItem(LS_KEY, JSON.stringify(next));
            }
          } catch (err2) {
            console.error("[Forum] createThread save error", err2);
          }
        })();
      }
    } else {
      const th = {
        id: (crypto?.randomUUID?.() || nanoid()),
        title,
        body,
        category: tCategory,
        createdAt: nowIso(),
        created_at: nowIso(),
        author_user_id: currentUser?.id || null,
        author_email: currentUser?.email || null,
        replies: [],
        attachments: tAttachments,
      };
      const next = [th, ...threads];
      setThreads(next);
      setExpandId(th.id);
      (async () => {
        try {
          if (storage?.saveJSON) {
            await storage.saveJSON(FILE_PATH, next);
          } else {
            localStorage.setItem(LS_KEY, JSON.stringify(next));
          }
        } catch (err) {
          console.error("[Forum] createThread save error", err);
        }
      })();
    }
    setTTitle("");
    setTBody("");
    setTCategory("general");
    setTAttachments([]);
    addNotification?.({
      world: "both",
      type: "forum-message",
      title: `New forum thread: ${title}`,
      body: body || "Tap to read the new discussion.",
      target: { entity: "forum", route: "/forum" },
      severity: "info",
      meta: {},
    });
  };

  const openReport = (thread) => {
    setReportingPost(thread);
    setIsReportOpen(true);
  };

  const closeReport = () => {
    setIsReportOpen(false);
    setReportingPost(null);
  };

  const addReply = async (tid) => {
    const thread = threads.find((t) => t.id === tid);
    const body = replyText.trim();
    if (!body && replyAttachments.length === 0) return;
    if (hasSupabase() && supabase) {
      try {
        const { data, error } = await supabase
          .from("forum_replies")
          .insert({
            thread_id: tid,
            body,
            attachments: replyAttachments,
            author_user_id: currentUser?.id || null,
            author_email: currentUser?.email || null,
            author_display_name: currentUser?.user_metadata?.full_name || currentUser?.email || null,
          })
          .select()
          .single();
        if (error) throw error;
        const mapped = {
          id: data.id,
          body: data.body,
          attachments: Array.isArray(data.attachments) ? data.attachments : [],
          author_user_id: data.author_user_id,
          author_email: data.author_email,
          author_display_name: data.author_display_name,
          createdAt: data.created_at,
        };
        setThreads((prev) => prev.map((t) => (t.id === tid ? { ...t, replies: [...(t.replies || []), mapped] } : t)));
      } catch (err) {
        console.warn("[Forum] Supabase addReply failed, falling back to local", err);
        setThreads((prev) =>
          prev.map((t) =>
            t.id === tid
              ? {
                  ...t,
                  replies: [
                    ...(t.replies || []),
                    {
                      id: nanoid(),
                      body,
                      createdAt: nowIso(),
                      attachments: replyAttachments,
                    },
                  ],
                }
              : t
          )
        );
      }
    } else {
      setThreads((prev) =>
        prev.map((t) =>
          t.id === tid
            ? {
                ...t,
                replies: [
                  ...(t.replies || []),
                  {
                    id: nanoid(),
                    body,
                    createdAt: nowIso(),
                    attachments: replyAttachments,
                  },
                ],
              }
            : t
        )
      );
    }
    setReplyText("");
    setReplyAttachments([]);
    if (isFollowing?.(tid)) {
      const snippet =
        body && body.length > 140 ? `${body.slice(0, 140)}…` : body || "Attachment posted";
      const title = thread?.title || "Forum thread";
      addNotification?.({
        world: "studio",
        type: "forum-reply",
        title: `New reply in ${title}`,
        body: snippet,
        target: { entity: "forum", route: `${ROUTES.FORUM}?thread=${tid}` },
        severity: "info",
        meta: { threadId: tid },
      });
    }
  };

  // File handling for thread creation
  const handleThreadFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    files.forEach(file => {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(`File "${file.name}" is too large. Max size is 5MB.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (evt) => {
        setTAttachments(prev => [
          ...prev,
          {
            name: file.name,
            data: evt.target.result, // base64 data URL
            type: file.type,
            size: file.size
          }
        ]);
      };
      reader.readAsDataURL(file);
    });
    
    // Reset input
    if (threadFileInputRef.current) {
      threadFileInputRef.current.value = '';
    }
  };

  // File handling for replies
  const handleReplyFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`File "${file.name}" is too large. Max size is 5MB.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (evt) => {
        setReplyAttachments(prev => [
          ...prev,
          {
            name: file.name,
            data: evt.target.result,
            type: file.type,
            size: file.size
          }
        ]);
      };
      reader.readAsDataURL(file);
    });
    
    if (replyFileInputRef.current) {
      replyFileInputRef.current.value = '';
    }
  };

  const removeThreadAttachment = (index) => {
    setTAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const removeReplyAttachment = (index) => {
    setReplyAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const removeThread = (tid) => {
    if (!confirm("Delete this thread?")) return;
    if (hasSupabase() && supabase) {
      (async () => {
        try {
          const { error } = await supabase.from("forum_threads").delete().eq("id", tid);
          if (error) throw error;
          setThreads((prev) => prev.filter((t) => t.id !== tid));
        } catch (err) {
          console.warn("[Forum] Supabase delete thread failed (perhaps not author)", err);
          setThreads((prev) => prev.filter((t) => t.id !== tid));
        }
      })();
    } else {
      setThreads((prev) => prev.filter((t) => t.id !== tid));
    }
    if (expandId === tid) setExpandId(null);
  };

  const clearAll = () => {
    if (!confirm("This will remove all threads locally. Continue?")) return;
    setThreads([]);
  };

  const openReportModal = (post) => {
    setReportingPost(post);
    setIsReportOpen(true);
  };

  return (
    <div style={shell}>
      <div style={header}>
        <div>
          <h1 style={h1}>Forum</h1>
          <p style={{ margin: 0, opacity: 0.75, fontSize: 12 }}>
            Ask questions, share tips, and discuss studio workflows.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            style={input}
            placeholder="Search threads…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button style={btn} onClick={clearAll} title="Remove all local threads">Clear All</button>
        </div>
      </div>

      {/* Create thread */}
      <div style={{ ...card, marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
          Start a thread
        </h3>
        <div style={{ display: "grid", gap: 8, maxWidth: 720 }}>
          <input
            style={input}
            placeholder="Thread title"
            value={tTitle}
            onChange={(e) => setTTitle(e.target.value)}
          />
          <select
            style={{ 
              ...input, 
              cursor: "pointer",
              backgroundColor: "rgba(255,255,255,0.06)",
              color: "#fff",
            }}
            value={tCategory}
            onChange={(e) => setTCategory(e.target.value)}
          >
            {CATEGORIES.filter(c => c.id !== "all").map((cat) => (
              <option 
                key={cat.id} 
                value={cat.id}
                style={{ 
                  backgroundColor: "#1f2937",
                  color: "#fff",
                }}
              >
                {cat.label}
              </option>
            ))}
          </select>
          <textarea
            style={area}
            placeholder="What's on your mind?"
            value={tBody}
            onChange={(e) => setTBody(e.target.value)}
          />
          
          {/* Attachment previews */}
          {tAttachments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {tAttachments.map((att, idx) => (
                <div key={idx} style={{ 
                  position: 'relative',
                  padding: 8,
                  borderRadius: 8,
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  maxWidth: 200
                }}>
                  {att.type?.startsWith('image/') ? (
                    <img src={att.data} alt={att.name} style={{ 
                      width: 60, 
                      height: 60, 
                      objectFit: 'cover', 
                      borderRadius: 6 
                    }} />
                  ) : (
                    <div style={{ 
                      width: 60, 
                      height: 60, 
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      textAlign: 'center',
                      padding: 4
                    }}>
                      📎 {att.name.split('.').pop()?.toUpperCase()}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {att.name}
                    </div>
                    <div style={{ fontSize: 10, opacity: 0.7 }}>
                      {formatFileSize(att.size)}
                    </div>
                  </div>
                  <button
                    onClick={() => removeThreadAttachment(idx)}
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: '#ff5555',
                      border: 'none',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0
                    }}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div style={{ display: "flex", gap: 8, flexWrap: 'wrap' }}>
            <button style={btn} onClick={createThread}>Post</button>
            <input
              ref={threadFileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt"
              onChange={handleThreadFileSelect}
              style={{ display: 'none' }}
            />
            <button 
              style={{ ...btn, display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={() => threadFileInputRef.current?.click()}
            >
              📎 Attach Files
            </button>
            <button
              style={{ ...btn, background: "transparent", opacity: 0.8 }}
              onClick={() => { setTTitle(""); setTBody(""); setTAttachments([]); }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div style={{ marginBottom: 16, display: "flex", gap: 6, flexWrap: "wrap" }}>
        {CATEGORIES.map((cat) => {
          const isActive = selectedCategory === cat.id;
          const hexToRgba = (hex, alpha) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
          };
          
          return (
            <button
              key={cat.id}
              style={{
                ...btn,
                background: isActive 
                  ? hexToRgba(cat.color, 0.25) 
                  : "rgba(255,255,255,0.06)",
                borderColor: isActive 
                  ? hexToRgba(cat.color, 0.5) 
                  : hexToRgba(cat.color, 0.3),
                color: isActive ? cat.color : "#e5e7eb",
                fontWeight: isActive ? 600 : 400,
                transition: "all 0.2s ease",
              }}
              onClick={() => setSelectedCategory(cat.id)}
            >
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Threads */}
      {!isLoading && filtered.length === 0 ? (
        <div style={{ ...card, textAlign: "center", color: "#ddd" }}>
          No threads yet. Be the first to post!
        </div>
      ) : (
        filtered.map((t) => (
          <div key={t.id} style={row}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {/* Category badge */}
                  {t.category && (() => {
                    const categoryData = CATEGORIES.find(c => c.id === t.category);
                    const catColor = categoryData?.color || "#94a3b8";
                    const hexToRgba = (hex, alpha) => {
                      const r = parseInt(hex.slice(1, 3), 16);
                      const g = parseInt(hex.slice(3, 5), 16);
                      const b = parseInt(hex.slice(5, 7), 16);
                      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                    };
                    
                    return (
                      <span style={{
                        fontSize: 11,
                        padding: "4px 10px",
                        borderRadius: 6,
                        background: hexToRgba(catColor, 0.15),
                        color: catColor,
                        fontWeight: 600,
                        border: `1px solid ${hexToRgba(catColor, 0.3)}`,
                        letterSpacing: "0.02em"
                      }}>
                        {categoryData?.label || "General"}
                      </span>
                    );
                  })()}
                  <h3 style={title}>{t.title}</h3>
                  {isFollowing?.(t.id) && <Star size={16} color="#fbbf24" fill="#fbbf24" />}
                </div>
                <p style={meta}>
                  {fromIso(t.createdAt)} • {t.replies?.length || 0} replies
                </p>
              </div>
              <div style={{ display: "flex", gap: 8, height: 36 }}>
                <button
                  style={{
                    ...btn,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: isFollowing?.(t.id)
                      ? "rgba(255,255,255,0.12)"
                      : "rgba(255,255,255,0.06)",
                    borderColor: "rgba(255,255,255,0.2)",
                  }}
                  onClick={() => toggleFollow?.(t.id)}
                  title={isFollowing?.(t.id) ? "Unfollow thread" : "Follow thread"}
                >
                  {isFollowing?.(t.id) ? <Star size={16} fill="#fbbf24" color="#fbbf24" /> : <StarOff size={16} />}
                  {isFollowing?.(t.id) ? "Following" : "Follow"}
                </button>
                <button style={btn} onClick={() => setExpandId(expandId === t.id ? null : t.id)}>
                  {expandId === t.id ? "Hide" : "View"}
                </button>
                <button
                  style={{
                    ...btn,
                    background: "rgba(255,255,255,0.04)",
                    color: "#f87171",
                    borderColor: "rgba(255,255,255,0.18)",
                    fontSize: 12,
                  }}
                  onClick={() => openReport({
                    ...t,
                    author_user_id: t.author_user_id || t.authorId || t.authorId,
                  })}
                  title="Report this post"
                >
                  Report
                </button>
                <button
                  style={{ ...btn, color: "#ffb3b3", borderColor: "rgba(255,255,255,0.2)" }}
                  onClick={() => removeThread(t.id)}
                  title="Delete thread"
                >
                  Delete
                </button>
              </div>
            </div>

            {expandId === t.id && (
              <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                {t.body && <div style={reply}>{t.body}</div>}
                
                {/* Thread attachments */}
                {t.attachments && t.attachments.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: -6 }}>
                    {t.attachments.map((att, idx) => (
                      <div key={idx}>
                        {att.type?.startsWith('image/') ? (
                          <img 
                            src={att.data} 
                            alt={att.name}
                            style={{ 
                              maxWidth: '100%',
                              maxHeight: 300,
                              borderRadius: 8,
                              cursor: 'pointer',
                              border: '1px solid rgba(255,255,255,0.15)'
                            }}
                            onClick={() => window.open(att.data, '_blank')}
                            title="Click to open in new tab"
                          />
                        ) : (
                          <a
                            href={att.data}
                            download={att.name}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '8px 12px',
                              borderRadius: 8,
                              background: 'rgba(255,255,255,0.08)',
                              border: '1px solid rgba(255,255,255,0.15)',
                              color: '#fff',
                              textDecoration: 'none',
                              fontSize: 13
                            }}
                          >
                            📎 {att.name} ({formatFileSize(att.size)})
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {(t.replies || []).map((r) => (
                  <div key={r.id} style={reply}>
                    <div style={{ whiteSpace: "pre-wrap" }}>{r.body}</div>
                    
                    {/* Reply attachments */}
                    {r.attachments && r.attachments.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                        {r.attachments.map((att, idx) => (
                          <div key={idx}>
                            {att.type?.startsWith('image/') ? (
                              <img 
                                src={att.data} 
                                alt={att.name}
                                style={{ 
                                  maxWidth: '100%',
                                  maxHeight: 200,
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  border: '1px solid rgba(255,255,255,0.15)'
                                }}
                                onClick={() => window.open(att.data, '_blank')}
                                title="Click to open in new tab"
                              />
                            ) : (
                              <a
                                href={att.data}
                                download={att.name}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  padding: '6px 10px',
                                  borderRadius: 6,
                                  background: 'rgba(255,255,255,0.08)',
                                  border: '1px solid rgba(255,255,255,0.15)',
                                  color: '#fff',
                                  textDecoration: 'none',
                                  fontSize: 12
                                }}
                              >
                                📎 {att.name}
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div style={{ opacity: 0.65, fontSize: 12, marginTop: 6 }}>
                      {fromIso(r.createdAt)}
                    </div>
                  </div>
                ))}

                <div style={{ display: "grid", gap: 8 }}>
                  <textarea
                    style={area}
                    placeholder="Write a reply…"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                  
                  {/* Reply attachment previews */}
                  {replyAttachments.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {replyAttachments.map((att, idx) => (
                        <div key={idx} style={{ 
                          position: 'relative',
                          padding: 6,
                          borderRadius: 6,
                          background: 'rgba(255,255,255,0.08)',
                          border: '1px solid rgba(255,255,255,0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          maxWidth: 150,
                          fontSize: 12
                        }}>
                          {att.type?.startsWith('image/') ? (
                            <img src={att.data} alt={att.name} style={{ 
                              width: 40, 
                              height: 40, 
                              objectFit: 'cover', 
                              borderRadius: 4 
                            }} />
                          ) : (
                            <div style={{ 
                              width: 40, 
                              height: 40, 
                              background: 'rgba(255,255,255,0.1)',
                              borderRadius: 4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 9
                            }}>
                              📎
                            </div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {att.name}
                            </div>
                          </div>
                          <button
                            onClick={() => removeReplyAttachment(idx)}
                            style={{
                              position: 'absolute',
                              top: -6,
                              right: -6,
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              background: '#ff5555',
                              border: 'none',
                              color: '#fff',
                              cursor: 'pointer',
                              fontSize: 11,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 0
                            }}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={btn} onClick={() => addReply(t.id)}>Reply</button>
                    <input
                      ref={replyFileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      onChange={handleReplyFileSelect}
                      style={{ display: 'none' }}
                    />
                    <button 
                      style={{ ...btn, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
                      onClick={() => replyFileInputRef.current?.click()}
                    >
                      📎 Attach
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))
      )}

      <ReportPostModal
        isOpen={isReportOpen}
        onClose={closeReport}
        post={reportingPost}
        currentUser={currentUser}
      />
    </div>
  );
}
